import type { DateSourceType, ViewDateSourceDTO } from '@tempo-io/roadmap-dto';
import type { ItemV2 } from '@/common/api/types/items';
import type { Schedule } from '@/common/api/types/schedules';

export type ItemDateStrings = readonly [
  start: string | null | undefined,
  finish: string | null | undefined,
];

export const DEFAULT_VIEW_DATE_SOURCE: DateSourceType = 'PLAN';

export const isForecastDateSource = (dateSource: ViewDateSourceDTO | undefined): boolean =>
  dateSource?.dateSourceType === 'FORECAST';

const isPlanDateSource = (dateSource: ViewDateSourceDTO | undefined): boolean =>
  !dateSource || dateSource.dateSourceType === 'PLAN';

type DateFieldName =
  | 'plannedStartDateTime'
  | 'plannedFinishDateTime'
  | 'earliestStartDateTime'
  | 'deadlineDateTime';

export type EditableDateFields = { startField: DateFieldName; finishField: DateFieldName };

/**
 * The item fields drag/resize should write for the given date source, or null
 * when the source is read-only (FORECAST is calculated, custom fields deferred).
 */
export function getEditableDateFields(
  dateSource: ViewDateSourceDTO | undefined,
): EditableDateFields | null {
  switch (dateSource?.dateSourceType) {
    case 'TARGET':
      return { startField: 'earliestStartDateTime', finishField: 'deadlineDateTime' };
    case 'FORECAST':
    case 'CUSTOM':
      return null;
    default:
      return { startField: 'plannedStartDateTime', finishField: 'plannedFinishDateTime' };
  }
}

/**
 * The item date pair (start, finish) that drives date-based visualizations,
 * resolved for the view's chosen date source. FORECAST reads a lazily-fetched
 * Schedule map keyed by item id; the other sources read fields off the item.
 */
export function resolveItemDateStrings(
  item: { id?: number } & Partial<
    Pick<
      ItemV2,
      | 'plannedStartDateTime'
      | 'plannedFinishDateTime'
      | 'earliestStartDateTime'
      | 'deadlineDateTime'
    >
  >,
  dateSource: ViewDateSourceDTO | undefined,
  scheduleById?: Record<number, Schedule> | undefined,
): ItemDateStrings {
  switch (dateSource?.dateSourceType) {
    case 'TARGET':
      return [item.earliestStartDateTime, item.deadlineDateTime];
    case 'FORECAST': {
      const schedule = item.id !== undefined ? scheduleById?.[item.id] : undefined;
      return [schedule?.forecastedStartDateTime, schedule?.forecastedFinishDateTime];
    }
    default:
      return [item.plannedStartDateTime, item.plannedFinishDateTime];
  }
}

type RemappableItem = Partial<
  Pick<
    ItemV2,
    'plannedStartDateTime' | 'plannedFinishDateTime' | 'earliestStartDateTime' | 'deadlineDateTime'
  >
> & { id?: number };

/**
 * Return a shallow copy of each item with plannedStart/FinishDateTime overwritten
 * by the view's chosen date source, so downstream date consumers stay unchanged.
 * PLAN is a no-op passthrough (returns the original array). The original date
 * fields (e.g. deadlineDateTime) remain intact on the copy for at-risk logic.
 */
export function remapItemDates<T extends RemappableItem>(
  items: readonly T[],
  dateSource: ViewDateSourceDTO | undefined,
  scheduleById?: Record<number, Schedule> | undefined,
): T[] {
  if (isPlanDateSource(dateSource)) return items as T[];

  return items.map((item) => {
    const [start, finish] = resolveItemDateStrings(
      {
        id: item.id,
        plannedStartDateTime: item.plannedStartDateTime ?? null,
        plannedFinishDateTime: item.plannedFinishDateTime ?? null,
        earliestStartDateTime: item.earliestStartDateTime ?? null,
        deadlineDateTime: item.deadlineDateTime ?? null,
      },
      dateSource,
      scheduleById,
    );
    return { ...item, plannedStartDateTime: start ?? null, plannedFinishDateTime: finish ?? null };
  });
}
